import { isPlatformBrowser } from '@angular/common';
import { HttpInterceptorFn } from '@angular/common/http';
import { computed, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import {
  GoogleAuthProvider,
  onAuthStateChanged,
  signInWithPopup,
  signOut as firebaseSignOut,
  User as FirebaseUser,
} from 'firebase/auth';
import { from, switchMap } from 'rxjs';
import { firebaseAuth } from './firebase';

/** Credit amounts are in credits (1 credit = $0.001). */
export interface Usage {
  requestsToday: number;
  creditsToday: number;
  inFlight: number;
  creditBalance: number;
  freeCreditsPerDay: number;
  requestsPerDay: number;
}

/** Free credits still unused today. They're spent before purchased ones, so they go first. */
export function freeCreditsLeft(u: Usage): number {
  return Math.max(0, u.freeCreditsPerDay - u.creditsToday);
}

export interface Me {
  id: string;
  type: 'google' | 'anonymous';
  email?: string;
  name?: string;
  isAdmin: boolean;
  usage: Usage;
  /** Rewards ($) earned by the user's nodes. Separate from credits. */
  rewardsUsd: number;
  /** Optional features this person can use, e.g. 'advanced'. */
  features?: string[];
  /** What an advanced turn's web searches cost: credits each, and at most how many per message. */
  search?: { credits: number; maxPerTurn: number };
}

const ANON_KEY = 'friendlai.session';
/** Set once the server has said this browser's account is the admin, so the menu can offer Admin before /api/me answers. */
const ADMIN_KEY = 'friendlai.admin';

function rememberAdmin(value?: boolean): boolean {
  try {
    if (value === true) localStorage.setItem(ADMIN_KEY, '1');
    else if (value === false) localStorage.removeItem(ADMIN_KEY);
    return localStorage.getItem(ADMIN_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Who the visitor is. Signed-in users authenticate with a Firebase ID token;
 * everyone else gets a signed guest session from the server. Either way,
 * `token()` returns the bearer token for /api calls.
 */
@Injectable({ providedIn: 'root' })
export class UserService {
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private firebaseUser = signal<FirebaseUser | null>(null);
  /** False until Firebase has restored any previous sign-in. */
  readonly ready = signal(false);
  readonly me = signal<Me | null>(null);

  readonly signedIn = computed(() => !!this.firebaseUser());
  readonly displayName = computed(
    () => this.firebaseUser()?.displayName || this.me()?.name || 'Guest',
  );
  readonly photoUrl = computed(() => this.firebaseUser()?.photoURL ?? null);
  readonly isAdmin = computed(() => !!this.me()?.isAdmin);
  private rememberedAdmin = signal(this.isBrowser && rememberAdmin());
  /** Whether to offer the admin pages: what the server said, or what it said last time until it answers. */
  readonly showAdmin = computed(() => (this.me() ? this.isAdmin() : this.rememberedAdmin()));
  readonly features = computed(() => this.me()?.features ?? []);
  /** Changes whenever the identity does; use it to re-run identity-specific requests. */
  readonly identityKey = computed(() =>
    this.ready() ? (this.firebaseUser()?.uid ?? 'guest') : null,
  );

  private anonPromise?: Promise<string>;

  constructor() {
    if (!this.isBrowser) return;
    // Sessions from before the auth rewrite aren't valid anymore.
    localStorage.removeItem('user');
    onAuthStateChanged(firebaseAuth(), (user) => {
      this.firebaseUser.set(user);
      this.ready.set(true);
      this.refreshMe();
    });
  }

  /** The bearer token for API calls, or null during SSR. */
  async token(): Promise<string | null> {
    if (!this.isBrowser) return null;
    const user = this.firebaseUser() ?? (await this.waitForAuth());
    if (user) return user.getIdToken();
    return this.anonymousToken();
  }

  private waitForAuth(): Promise<FirebaseUser | null> {
    if (this.ready()) return Promise.resolve(this.firebaseUser());
    return firebaseAuth()
      .authStateReady()
      .then(() => firebaseAuth().currentUser);
  }

  private anonymousToken(): Promise<string> {
    const saved = localStorage.getItem(ANON_KEY);
    if (saved) return Promise.resolve(saved);
    this.anonPromise ??= fetch('/api/session', { method: 'POST' })
      .then((r) => {
        if (!r.ok) throw new Error('Could not start a session.');
        return r.json();
      })
      .then(({ token }: { token: string }) => {
        localStorage.setItem(ANON_KEY, token);
        return token;
      })
      .finally(() => (this.anonPromise = undefined));
    return this.anonPromise;
  }

  async refreshMe(retry = true): Promise<void> {
    try {
      const token = await this.token();
      if (!token) return;
      const res = await fetch('/api/me', { headers: { Authorization: `Bearer ${token}` } });
      if (res.status === 401 && !this.firebaseUser()) {
        // Guest session no longer valid (e.g. new server secret): start a new one.
        localStorage.removeItem(ANON_KEY);
        if (retry) return this.refreshMe(false);
        this.me.set(null);
        return;
      }
      const me: Me | null = res.ok ? await res.json() : null;
      this.me.set(me);
      // Only a signed-in answer settles it; a guest session says nothing about the account.
      if (me && this.firebaseUser()) this.rememberedAdmin.set(rememberAdmin(me.isAdmin));
    } catch {
      this.me.set(null);
    }
  }

  async signIn() {
    const auth = firebaseAuth();
    auth.useDeviceLanguage();
    const result = await signInWithPopup(auth, new GoogleAuthProvider());
    // Bring guest history along to the account.
    const anon = localStorage.getItem(ANON_KEY);
    if (anon) {
      await fetch('/api/me/claim', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${await result.user.getIdToken()}`,
        },
        body: JSON.stringify({ anonymousToken: anon }),
      }).catch(() => {});
      localStorage.removeItem(ANON_KEY);
    }
    await this.refreshMe();
  }

  async signOut() {
    await firebaseSignOut(firebaseAuth());
    this.rememberedAdmin.set(rememberAdmin(false));
    this.me.set(null);
  }
}

/** Adds the visitor's bearer token to same-origin /api requests. */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const users = inject(UserService);
  if (!req.url.startsWith('/api/') || req.headers.has('Authorization')) return next(req);
  return from(users.token()).pipe(
    switchMap((token) =>
      next(token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req),
    ),
  );
};
